/** 停机屏「停机期间与超时拒单（纸上盘按策略）」（屏规格 `docs/design/screens/halt.md`，#4752）。
 *
 * 每条策略读 `GET /orders?strategy=<sid>&status=REJECTED`：后端取该策略最近一次纸上盘运行的账（累计回放，
 * 含历次拒单），只做状态过滤；这里只把各策略的结果并成一张表，不推导拒单原因。
 */

import type { Book, G2Order } from "../api/g2";
import { ApiError } from "../api/errors";

export interface RejectedRow {
  strategy_id: string;
  run_id: string | null;
  trade_date: string | null;
  client_order_id: string;
  instrument_id: string;
  ts_event: number;
  reason: string | null;
  /** 原因里带「超时 / timeout」：纸上盘委托超时只记台账，不停机（execution-kernel「停机停手」）。 */
  timeout: boolean;
}

export type StrategyBook = { strategy: string; book: Book<G2Order[]> } | { strategy: string; error: unknown };

/** 停机原因的标签按当前账的模式标（#4756）：当前账可能是纸上盘的账，不能一律写「回测账」。
 * 空账 / 旧后端没给模式时只写「停机原因」，不替它认定是哪本账。 */
export function haltReasonLabel(ledgerMode: string | null | undefined): string {
  if (ledgerMode === "backtest") return "回测账停机原因";
  if (ledgerMode === "paper") return "纸上盘停机原因";
  return "停机原因";
}

/** 拒单的标的：账上有就原样显示；#4756 之前的旧账本里风控拒单没记标的，显示「缺」而不是留空或「—」。 */
export function rejectedInstrument(instrumentId: string | null | undefined): { text: string; missing: boolean } {
  const value = (instrumentId ?? "").trim();
  return value ? { text: value, missing: false } : { text: "缺", missing: true };
}

export function isTimeoutReason(reason: string | null): boolean {
  return reason !== null && /timeout|超时/i.test(reason);
}

/** 合并各策略拒单，按时间倒序。该策略还没有纸上盘运行（404）不是错误，跳过；其它错误照抛给屏的错误态。 */
export function rejectedLedger(items: StrategyBook[]): RejectedRow[] {
  const rows: RejectedRow[] = [];
  for (const item of items) {
    if ("error" in item) {
      if (item.error instanceof ApiError && item.error.status === 404) continue;
      throw item.error;
    }
    for (const order of item.book.rows) {
      if (order.status !== "REJECTED") continue;
      rows.push({
        strategy_id: item.book.strategy_id ?? item.strategy,
        run_id: item.book.run_id,
        trade_date: item.book.trade_date,
        client_order_id: order.client_order_id,
        instrument_id: order.instrument_id,
        ts_event: order.ts_event,
        reason: order.reason,
        timeout: isTimeoutReason(order.reason),
      });
    }
  }
  return rows.sort((a, b) => b.ts_event - a.ts_event);
}
